import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell, ResponsiveContainer } from 'recharts';
import { Monitor } from 'lucide-react';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../ui/chart';

interface BrowserData {
  browser: string;
  count: number;
}

interface BrowserBreakdownChartProps {
  data: BrowserData[];
  totalScans: number;
  className?: string;
}

const chartConfig = {
  chrome: { label: "Chrome", color: "#D97756" },
  firefox: { label: "Firefox", color: "#E8927C" },
  safari: { label: "Safari", color: "#7D9B76" },
  edge: { label: "Edge", color: "#D4A574" },
  other: { label: "Other", color: "#9A8F85" },
};

// Warm theme colors for browsers
const BROWSER_COLORS: Record<string, string> = {
  chrome: '#D97756',
  firefox: '#E8927C',
  safari: '#7D9B76',
  edge: '#D4A574',
  opera: '#C4654A',
  default: '#9A8F85',
};

export function BrowserBreakdownChart({ data, totalScans, className = '' }: BrowserBreakdownChartProps) {
  // Use provided data only (no mock data)
  const chartData = (data || [])
    .sort((a, b) => b.count - a.count)
    .slice(0, 5)
    .map((item) => ({
      browser: item.browser.charAt(0).toUpperCase() + item.browser.slice(1),
      scans: item.count,
      percentage: totalScans > 0 ? ((item.count / totalScans) * 100).toFixed(1) : '0',
      color: BROWSER_COLORS[item.browser.toLowerCase()] || BROWSER_COLORS.default,
    }));

  if (!chartData || chartData.length === 0) {
    return (
      <div className={className}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-[#2D2520] flex items-center gap-2">
            <Monitor className="w-4 h-4 text-[#D97756]" />
            Browser Breakdown
          </h3>
        </div>
        <div className="flex items-center justify-center h-48 bg-[#F5F0EB] rounded-lg border border-dashed border-[#E5DED6]">
          <div className="text-center">
            <Monitor className="w-10 h-10 text-[#9A8F85] mx-auto mb-2" />
            <p className="text-sm text-[#5C524A]">No browser data yet</p>
          </div>
        </div>
      </div>
    );
  }

  // Top browser
  const topBrowser = chartData[0];

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-[#2D2520] flex items-center gap-2">
          <Monitor className="w-4 h-4 text-[#D97756]" />
          Browser Breakdown
        </h3>
      </div>
      <p className="text-xs text-[#5C524A] mb-3">
        {topBrowser.browser} leads ({topBrowser.percentage}%)
      </p>

      <div className="w-full overflow-hidden">
        <ChartContainer config={chartConfig} className="h-36 w-full">
          <BarChart
            data={chartData}
            margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis
              dataKey="browser"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 9, fill: '#5C524A' }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 9, fill: '#5C524A' }}
              tickFormatter={(value) => value.toLocaleString()}
              width={30}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  formatter={(value, name) => {
                    const browserData = chartData.find(d => d.browser === name);
                    return [`${value} scans (${browserData?.percentage || 0}%)`, ''];
                  }}
                  labelFormatter={(label) => `Browser: ${label}`}
                />
              }
            />
            <Bar
              dataKey="scans"
              radius={[3, 3, 0, 0]}
              animationBegin={200}
              animationDuration={1200}
              animationEasing="ease-out"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ChartContainer>
      </div>

      {/* Compact Legend */}
      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1">
        {chartData.slice(0, 4).map((browser) => (
          <div key={browser.browser} className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: browser.color }}
              />
              <span className="text-xs text-[#5C524A]">{browser.browser}</span>
            </div>
            <span className="text-xs font-medium text-[#2D2520]">
              {browser.percentage}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
